import { useMutation } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { requestPasswordReset, resetPassword } from '@/shared/api/auth';
import { Button, ErrorMessage, PasswordField, PasswordStrength, TextField } from '@/shared/ui';

import styles from './forms.module.css';

/** Asks for a reset link; says the same thing whether or not the address is known. */
export function ForgotPasswordForm(): React.JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const mutation = useMutation({ mutationFn: () => requestPasswordReset(email) });

  if (mutation.isSuccess) {
    return (
      <p className={styles.notice} role="status">
        {t('auth.forgotSent')}
      </p>
    );
  }

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <p>{t('auth.forgotHint')}</p>
      <TextField
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        {t('auth.forgotSubmit')}
      </Button>
    </form>
  );
}

/** Sets a new password with the token from the reset link. */
export function ResetPasswordForm({
  token,
  onDone,
}: {
  token: string;
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const mutation = useMutation({
    mutationFn: () => resetPassword(token, password),
    onSuccess: onDone,
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <PasswordField
        label={t('auth.newPassword')}
        autoComplete="new-password"
        required
        minLength={8}
        hint={t('auth.passwordHint')}
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
      />
      <PasswordStrength password={password} />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        {t('auth.resetSubmit')}
      </Button>
    </form>
  );
}
